import isNil from 'lodash-es/isNil'
import isUndefined from 'lodash-es/isUndefined'
import { FunctionComponent, useMemo } from 'react'
import Blur from '@/components/common/blur'
import { Duration } from '@/components/common/duration-select'
import KolDetailStatistic, {
  KolDetailStatisticProps,
} from '@/components/kol/common/kol-detail-statistic'
import MetricPrTooltip from '@/components/kol/common/metric-pr-tooltip'
import { mockKolInfoKpi } from '@/constants/kol-detail/info-kpi'
import { mockKolPr } from '@/constants/kol-detail/metric-performance'
import {
  DURATION_MONTH_INTEGER_MAP,
  KolDetailDuration,
} from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KpiSchema } from '@/types/schema/kpi-schema'
import evaluateMetricPR from '@/utils/kol/evaluate-metric-pr'

interface FansUpRateStatisticProps {
  kpi?: KpiSchema
  loading?: KolDetailStatisticProps['loading']
  duration?: KolDetailDuration
  isKolInfoLocked: boolean
  showMockData?: boolean
}

const FansUpRateStatistic: FunctionComponent<FansUpRateStatisticProps> = ({
  showMockData,
  loading,
  duration = Duration.ThreeMonths,
  kpi,
  isKolInfoLocked,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const hiddenData = isKolInfoLocked && !showMockData

  const [rate, performance, pr] = useMemo(() => {
    if (isUndefined(kpi)) {
      return []
    }

    if (isKolInfoLocked) {
      return [
        mockKolInfoKpi.fansUpRate3m,
        mockKolInfoKpi.fansUpRatePerformance3m,
        mockKolPr.fansUpRatePr,
      ]
    }

    const fansUpRate =
      duration === Duration.ThreeMonths ? kpi.fansUpRate3m : kpi.fansUpRate6m

    const performance =
      duration === Duration.ThreeMonths
        ? kpi.fansUpRatePerformance3m
        : kpi.fansUpRatePerformance6m

    const pr = evaluateMetricPR(fansUpRate, performance)

    return [fansUpRate, performance, pr]
  }, [kpi, duration, isKolInfoLocked])

  return (
    <KolDetailStatistic
      loading={loading}
      suffix={
        <MetricPrTooltip
          hiddenData={isKolInfoLocked && !showMockData}
          metricTypeI18nId='kol:platform_data_fans_up_rate'
          performancePr50={performance?.pr50}
          pr={pr}
        />
      }
      title={formatMessage({ id: 'kol:platform_data_fans_up_rate' })}
      tooltip={{
        title: formatMessage(
          {
            id: 'kol:platform_data_fans_up_rate_tooltip',
          },
          { n: DURATION_MONTH_INTEGER_MAP[duration] },
        ),
      }}
      value={
        isNil(rate) || rate === 0 ? (
          'noData'
        ) : (
          <Blur activate={hiddenData}>
            {formatNumber(rate, {
              isPercentageDisplay: true,
            })}
          </Blur>
        )
      }
    />
  )
}

export default FansUpRateStatistic
